import { Mail, Phone, Shield } from 'lucide-react';
import SettingsGroup from './SettingsGroup';
import SettingsRow from './SettingsRow';
import { tenantDetails } from '../../../helpers/utils/types';
import { formatPhoneNumber } from '../../../helpers/utils/utils';

type SecurityCardProps = {
  tenantData: tenantDetails | null;
  enabledMfaCount: number;
  onResetPassword: () => void;
  onManageMfa: () => void;
  onAddPhone: () => void;
  onEditPhone: () => void;
  onVerifyPhone: () => void;
};

const verifiedBadge = (
  <span className='rounded-full bg-[#ECFDF3] px-2 py-1 text-[11px] font-semibold text-[#027A48]'>
    Verified
  </span>
);

const unverifiedBadge = (
  <span className='rounded-full bg-[#FEF3F2] px-2 py-1 text-[11px] font-semibold text-[#B42318]'>
    Unverified
  </span>
);

const actionButton = (label: string, onClick: () => void) => (
  <button
    className='text-[13px] font-semibold text-[#0055FF] hover:underline'
    onClick={onClick}
  >
    {label}
  </button>
);

const SecurityCard = ({
  tenantData,
  enabledMfaCount,
  onResetPassword,
  onManageMfa,
  onAddPhone,
  onEditPhone,
  onVerifyPhone,
}: SecurityCardProps) => {
  const hasPhone = Boolean(tenantData?.user?.phoneNumber);
  const isPhoneVerified = Boolean(tenantData?.user?.isPhoneVerified);

  const phoneAction = (() => {
    if (!hasPhone) {
      return actionButton('Add', onAddPhone);
    }

    if (!isPhoneVerified) {
      return (
        <div className='flex items-center gap-2'>
          {unverifiedBadge}
          {actionButton('Verify', onVerifyPhone)}
          {actionButton('Edit', onEditPhone)}
        </div>
      );
    }

    return (
      <div className='flex items-center gap-2'>
        {verifiedBadge}
        {actionButton('Edit', onEditPhone)}
      </div>
    );
  })();

  return (
    <SettingsGroup title='Security'>
      <SettingsRow
        icon={<Shield size={14} />}
        label='Password'
        value='Keep your account secure'
        action={
          <button
            className='text-[13px] font-semibold text-[#0055FF] hover:underline'
            onClick={onResetPassword}
          >
            Change
          </button>
        }
      />
      <SettingsRow
        icon={<Shield size={14} />}
        label='Two-factor authentication'
        value={enabledMfaCount > 0 ? `${enabledMfaCount} method(s) enabled` : 'No methods enabled'}
        action={
          <button
            className='text-[13px] font-semibold text-[#0055FF] hover:underline'
            onClick={onManageMfa}
          >
            {enabledMfaCount > 0 ? 'Manage' : 'Setup'}
          </button>
        }
      />
      <SettingsRow
        icon={<Mail size={14} />}
        label='Email'
        value={tenantData?.user?.email || '--'}
        action={
          tenantData?.user?.isEmailVerified ? (
            verifiedBadge
          ) : (
            unverifiedBadge
          )
        }
      />
      <SettingsRow
        icon={<Phone size={14} />}
        label='Phone'
        value={hasPhone ? formatPhoneNumber(tenantData!.user!.phoneNumber!) : '--'}
        action={phoneAction}
      />
    </SettingsGroup>
  );
};

export default SecurityCard;
